import { generatePath, useMatch } from "react-router";
import useClientContext from "../../context/useClientContext";
import { pageRoutes } from "../../routes";
import MenuLinkItem from "../common/MenuLinkItem";
import SubMenuList from "./SubMenuList";

const SettingsMenu = () => {
  const {
    hasAnyPermission,
    isFundAdmin,
    clientCode,
    reportingPortalApp,
    isAllowedToAccessAllEntities,
    isPortalFeatureEnabled,
  } = useClientContext();

  const routePattern = `/:clientCode/${pageRoutes.settings}/:page/*`;
  const routeMatch = useMatch(routePattern);
  const currentPage = routeMatch?.params["page"];

  const hasAccessToOrganizationUsers = hasAnyPermission(["ManageOrganizationUsers", "ViewOrganizationUsers"]);

  const hasAccessToCompanySettings = hasAnyPermission(["ViewCompanySettings", "ManageCompanySettings"]);

  const hasAccessToIntegrationSettings = hasAnyPermission(["ViewClientIntegrations", "ManageClientIntegrations"]);

  const hasAccessToImportData =
    isAllowedToAccessAllEntities &&
    hasAnyPermission([
      "ViewInvestorRelationsDataImports",
      "ManageInvestorRelationsDataImports",
      "ViewPortfolioMonitoringDataImports",
      "ManagePortfolioMonitoringDataImports",
      "ViewExpenseManagementDataImports",
      "ManageExpenseManagementDataImports",
    ]);

  const hasAccessToOrganizationDataModel = hasAnyPermission([
    "ViewOrganizationDataModel",
    "ManageOrganizationDataModel",
  ]);

  const hasAccessToActivityLog = hasAnyPermission(["ViewActivityLog"]);

  const buildFullPath = (page: string) => generatePath(routePattern, { clientCode, page });

  return (
    <>
      {isFundAdmin && (
        <SubMenuList>
          {hasAccessToOrganizationUsers && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.settingsOrganizationContacts}
              title="Organization Contacts"
              href={buildFullPath(pageRoutes.settingsOrganizationContacts)}
            />
          )}
          {hasAccessToOrganizationUsers && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.settingsMembers}
              title="Members"
              href={buildFullPath(pageRoutes.settingsMembers)}
            />
          )}
          {hasAccessToOrganizationUsers && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.settingsUserGroups}
              title="User Groups"
              href={buildFullPath(pageRoutes.settingsUserGroups)}
            />
          )}
          {hasAccessToOrganizationDataModel && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.dataModel}
              title="Data Model"
              href={buildFullPath(pageRoutes.dataModel)}
            />
          )}
        </SubMenuList>
      )}

      {!isFundAdmin && (
        <SubMenuList>
          {hasAccessToCompanySettings && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.settingsCompanyGeneral}
              title="General"
              href={buildFullPath(pageRoutes.settingsCompanyGeneral)}
            />
          )}
          {hasAccessToIntegrationSettings && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.settingsIntegrations}
              title="Apps & Integrations"
              href={buildFullPath(pageRoutes.settingsIntegrations)}
            />
          )}
          {hasAccessToImportData && !!reportingPortalApp && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.importData}
              title="Import Data"
              href={buildFullPath(pageRoutes.importData)}
            />
          )}
          {hasAccessToActivityLog && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.settingsActivityLog}
              title="Activity Log"
              href={buildFullPath(pageRoutes.settingsActivityLog)}
            />
          )}
          {hasAccessToCompanySettings && isPortalFeatureEnabled("CustomObjectsEnabled") && (
            <MenuLinkItem
              subItem
              active={currentPage === pageRoutes.records}
              title="Other Records"
              href={buildFullPath(pageRoutes.records)}
            />
          )}
        </SubMenuList>
      )}
    </>
  );
};

export default SettingsMenu;
